import { forwardRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { selectItemRowClassName, selectTriggerClassName } from '@/components/ui/select'
import {
  UNLOCODE_SEARCH_MIN_QUERY_LENGTH,
  useUnlocodeSearch,
  type UnlocodeSearchItem
} from '@/features/installations/hooks/useUnlocodes'
import { cn } from '@/shared/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

export type InstallationUnlocodeInputProps = {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  countryCodeFilter: string | null
  disabled?: boolean
  triggerClassName?: string
} & Pick<
  React.ComponentPropsWithoutRef<'div'>,
  'id' | 'aria-describedby' | 'aria-labelledby' | 'className'
>

function itemLabel(item: UnlocodeSearchItem): string {
  return `${item.code} (${item.name})`
}

/**
 * Server-backed searchable UN/LOCODE (`GET /v1/config/unlocodes`).
 * Select-style trigger opens a dialog to search and pick a location.
 */
export const InstallationUnlocodeInput = forwardRef<HTMLDivElement, InstallationUnlocodeInputProps>(
  function InstallationUnlocodeInput(
    {
      value,
      onValueChange,
      placeholder,
      countryCodeFilter,
      disabled,
      triggerClassName,
      className,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-labelledby': ariaLabelledBy,
      ...rest
    },
    reference
  ) {
    const { t } = useTranslation('installations')
    const [dialogOpen, setDialogOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    const countryUpper = (countryCodeFilter ?? '').trim().toUpperCase()
    const hasValidCountry = countryUpper.length === 2
    const trimmedValue = value.trim()

    const {
      data: listItems,
      isPending,
      isError,
      isFetching
    } = useUnlocodeSearch({
      country: countryCodeFilter,
      query: searchQuery,
      enabled: dialogOpen && hasValidCountry
    })

    const resolveQuery =
      !dialogOpen && trimmedValue.length >= UNLOCODE_SEARCH_MIN_QUERY_LENGTH ? trimmedValue : ''

    const { data: resolveItems } = useUnlocodeSearch({
      country: countryCodeFilter,
      query: resolveQuery,
      enabled: !dialogOpen && hasValidCountry && resolveQuery.length > 0
    })

    const selectedItem = useMemo((): UnlocodeSearchItem | null => {
      if (!trimmedValue) {
        return null
      }
      const match = resolveItems?.find(item => item.code === trimmedValue)
      if (match) {
        return match
      }
      return {
        code: trimmedValue,
        name: t('create.fields.unlocode.current')
      }
    }, [resolveItems, t, trimmedValue])

    const listRows = useMemo(() => {
      const items = listItems ?? []
      if (trimmedValue && !items.some(row => row.code === trimmedValue)) {
        return [
          {
            code: trimmedValue,
            name: t('create.fields.unlocode.current')
          },
          ...items
        ]
      }
      return items
    }, [listItems, t, trimmedValue])

    const fieldDisabled = disabled || !hasValidCountry
    const showClear = trimmedValue.length > 0 && !disabled
    const triggerShowsPlaceholder = selectedItem == null
    const triggerLabel = selectedItem ? itemLabel(selectedItem) : placeholder
    const awaitingSearchResults = dialogOpen && (isPending || isFetching)
    const showTypeHint =
      dialogOpen &&
      searchQuery.trim().length < UNLOCODE_SEARCH_MIN_QUERY_LENGTH &&
      !awaitingSearchResults
    const showLoadError = isError
    const showLoadingMessage =
      awaitingSearchResults && listRows.length === 0 && !showTypeHint && !showLoadError
    const showResultsList = !showLoadError && !showTypeHint && !showLoadingMessage
    const showNoMatches =
      showResultsList &&
      listRows.length === 0 &&
      searchQuery.trim().length >= UNLOCODE_SEARCH_MIN_QUERY_LENGTH

    const triggerClassNames = cn(
      selectTriggerClassName,
      'h-9 min-w-0 flex-1 data-[placeholder]:text-muted-foreground',
      triggerShowsPlaceholder && 'text-muted-foreground'
    )

    function selectRow(row: UnlocodeSearchItem) {
      onValueChange(row.code)
      setDialogOpen(false)
    }

    function clearValue() {
      onValueChange('')
    }

    return (
      <div
        ref={reference}
        id={id}
        aria-describedby={ariaDescribedBy}
        aria-labelledby={ariaLabelledBy}
        className={cn('w-full min-w-0', triggerClassName, className)}
        {...rest}
      >
        <Dialog
          open={dialogOpen}
          onOpenChange={nextOpen => {
            setDialogOpen(nextOpen)
            if (!nextOpen) {
              setSearchQuery('')
            }
          }}
        >
          <div className="flex w-full min-w-0 gap-1">
            <DialogTrigger asChild disabled={fieldDisabled}>
              <button
                type="button"
                data-size="default"
                data-placeholder={triggerShowsPlaceholder ? '' : undefined}
                disabled={fieldDisabled}
                className={triggerClassNames}
              >
                <span
                  data-slot="select-value"
                  className="line-clamp-1 flex min-w-0 flex-1 items-center gap-2 text-start"
                >
                  {triggerLabel}
                </span>
                <ChevronDownIcon className="size-4 shrink-0 opacity-50" aria-hidden />
              </button>
            </DialogTrigger>
            {showClear ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="size-9 shrink-0 text-muted-foreground"
                aria-label={t('create.fields.unlocode.clear')}
                onClick={clearValue}
              >
                <XIcon className="size-4" />
              </Button>
            ) : null}
          </div>
          <DialogContent className="flex h-[min(18rem,90vh)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md">
            <DialogHeader className="gap-1 border-b border-border px-4 py-3 text-start">
              <DialogTitle>{t('create.fields.unlocode.label')}</DialogTitle>
            </DialogHeader>
            <div className="shrink-0 border-b border-border px-4 py-3">
              <Input
                autoFocus
                value={searchQuery}
                placeholder={t('create.fields.unlocode.searchPlaceholder')}
                onChange={event => {
                  setSearchQuery(event.target.value)
                }}
              />
            </div>
            <div
              role="listbox"
              className="min-h-0 flex-1 overflow-y-auto p-1"
              aria-label={t('create.fields.unlocode.label')}
            >
              {showLoadError && (
                <p className="px-2 py-3 text-center text-sm text-destructive">
                  {t('create.fields.unlocode.loadError')}
                </p>
              )}
              {showLoadingMessage && (
                <div className="px-2">
                  <Skeleton className="h-8 w-full" />
                </div>
              )}
              {showTypeHint && (
                <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                  {t('create.fields.unlocode.typeToSearch')}
                </p>
              )}

              {showNoMatches && (
                <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                  {t('create.fields.unlocode.noMatches')}
                </p>
              )}
              {showResultsList
                ? listRows.map(row => {
                    const isSelected = row.code === trimmedValue
                    return (
                      <button
                        key={row.code}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          selectItemRowClassName,
                          isSelected && 'bg-accent text-accent-foreground'
                        )}
                        onClick={() => {
                          selectRow(row)
                        }}
                      >
                        <span className="truncate">{itemLabel(row)}</span>
                        <span className="absolute inset-e-2 flex size-3.5 items-center justify-center">
                          {isSelected ? <CheckIcon className="size-4" aria-hidden /> : null}
                        </span>
                      </button>
                    )
                  })
                : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }
)
